import React from 'react'
import { makeStyles } from '@mui/styles'
import { Dialog } from '@mui/material'
import ThemeInterface from '@/theme-interface'
import OnboardingV2 from '@/modules/onboardingV2/onboarding'
import { registrationBackdrop } from '@/modules/onboardingV2/helpers/registration-backdrop'
import isAdditionalTenant from '@/modules/onboardingV2/helpers/is-additional-tenant'
import { isMeinBuero } from '@/utils/build-env'
import { useAppSelector } from '@/redux/store'

interface OnboardingModalProps {
  open?: boolean
  onClose: () => void
}

interface StyleProps {
  isMbo: boolean
  isTaxConsultant: boolean
  isRegistrationFlow: boolean
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  paper: ({ isMbo, isTaxConsultant, isRegistrationFlow }: StyleProps) => ({
    width: '100%',
    height: '100%',
    margin: 0,
    maxHeight: '100%',
    maxWidth: '100%',
    // the registration cards float from 768 px on, below they are a full screen page (like the login)
    [`@media (min-width: ${isRegistrationFlow ? '768' : (isMbo ? '800' : '1034')}px)`]: isRegistrationFlow
      ? {
        width: 560,
        height: 'auto',
        maxHeight: 'calc(100% - 64px)',
        borderRadius: theme.borderRadiusExtraExtraLarge,
      }
      : {
        width: 900,
        height: isMbo ? (isTaxConsultant ? 710 : 'auto') : 690,
        minHeight: isMbo ? (isTaxConsultant ? 710 : 630) : 690,
      },
  }),
  registrationBackdrop: {
    ...registrationBackdrop(theme),
  },
}))

const OnboardingModal = ({ onClose, open = false }: OnboardingModalProps) => {
  const isMbo = isMeinBuero()
  const { subscriptions } = useAppSelector(state => state.tenant.info)
  const tenantRestores = useAppSelector(state => state.user.info.activeTenant.tenantRestores)
  const isTaxConsultant = !!subscriptions?.filter(s => s.isTaxConsultant === true)?.length
  const { tenants, activeTenant } = useAppSelector(state => state.user.info)
  // orgaMAX sign-up (password or AVV, then the short question) uses the compact registration cards;
  // a further company of an existing user keeps the old AVV modal
  const isRegistrationFlow = !isMbo && !isTaxConsultant && !isAdditionalTenant({ tenantId: activeTenant?.tenantId, tenants })
  const classes = useStyles({ isMbo, isTaxConsultant, isRegistrationFlow })
  const restoreTokenAvailable = !!/restore\/?.*/.test(window.location.pathname) || !!tenantRestores?.length
  if (restoreTokenAvailable) {
    return null
  }

  return (
    <Dialog
      maxWidth={false}
      disableEscapeKeyDown
      scroll='paper'
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return false
        }

        onClose?.()
      }}
      classes={{
        paper: classes.paper,
      }}
      slotProps={isRegistrationFlow ? { backdrop: { className: classes.registrationBackdrop } } : undefined}
      data-qs-id='onboarding-modal'
    >
      <OnboardingV2 onClose={onClose} />
    </Dialog>
  )
}

export default OnboardingModal
